import { debounce } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn, useForm, standardSchemaResolver, useWatch, useFormState } from '@fiverr-private/forms';
import { useIdentificationContext, extractEmailDomain, services } from '@fiverr-private/identification_core';
import { createSignInSchema, createSignUpSchema, FormSchema } from '../EmailIdentificationStep.schema';
import { isPotentialEmail } from '../../utils/isPotentialEmail';
import { globalBigQueryReporter } from '../../../../events';
import { translate } from '../../../../utils/translate';
import { VALIDATION_DEBOUNCE_TIME } from '../../common/constants';

interface ValidateArgs {
    value: string;
    setError: UseFormReturn<FormSchema>['setError'];
    clearErrors: UseFormReturn<FormSchema>['clearErrors'];
}

type SignInLoginServerError = 'format_username_email' | 'format_email' | 'email' | 'email_format' | 'illegal_domain';

type UseFormStateReturn = ReturnType<typeof useFormState<FormSchema>>;

export const useEmailIdentificationForm = () => {
    const [loading, setLoading] = useState(false);
    const { mode: initialMode } = useIdentificationContext();
    const [isSignUp, setIsSignUp] = useState(initialMode === 'sign_up');

    const schema = useMemo(() => (isSignUp ? createSignUpSchema() : createSignInSchema()), [isSignUp]);

    const methods = useForm<FormSchema>({
        mode: 'onChange',
        resolver: standardSchemaResolver(schema),
        defaultValues: {
            usernameOrEmail: '',
            password: '',
        },
    });

    const { errors } = useFormState<FormSchema>({ control: methods.control, name: 'password' });

    const password = useWatch<FormSchema>({ control: methods.control, name: 'password' });
    const usernameOrEmail = useWatch<FormSchema>({ control: methods.control, name: 'usernameOrEmail' });

    const verifyEmailDebounced = useMemo(() => {
        const verifyEmail = async ({ value, setError, clearErrors }: ValidateArgs) => {
            setLoading(true);
            const result = await services.validateEmailForIdentification(value);
            setLoading(false);

            if (!result) {
                return;
            }
            const { exists, valid, errorKeys } = result;
            if (exists) {
                setIsSignUp(false);
                clearErrors('usernameOrEmail');
                return;
            }

            if (valid) {
                setIsSignUp(true);
            } else {
                const errorKey: SignInLoginServerError =
                    Array.isArray(errorKeys) && errorKeys[0] ? errorKeys[0] : 'email';
                setError('usernameOrEmail', {
                    message: translate(`validation.identification_login.${errorKey}`),
                });
            }
        };
        return debounce(verifyEmail, VALIDATION_DEBOUNCE_TIME);
    }, [setLoading]);

    const verifyUsernameDebounced = useMemo(() => {
        const verifyUsername = async ({ value, setError, clearErrors }: ValidateArgs) => {
            setLoading(true);
            const result = await services.validateUsernameForIdentification(value);
            setLoading(false);

            if (!result) {
                return;
            }

            const { exists } = result;

            if (exists) {
                setIsSignUp(false);
                clearErrors('usernameOrEmail');
                return;
            }

            setError('usernameOrEmail', {
                message: translate('validation.identification_login.format_username_email'),
            });
        };
        return debounce(verifyUsername, VALIDATION_DEBOUNCE_TIME);
    }, [setLoading]);

    const cleanupDebouncedMethods = useCallback(() => {
        verifyEmailDebounced.cancel();
        verifyUsernameDebounced.cancel();
    }, [verifyEmailDebounced, verifyUsernameDebounced]);

    const onUsernameOrEmailChange = async (value: string) => {
        cleanupDebouncedMethods();

        methods.clearErrors('usernameOrEmail');
        if (!isSignUp) {
            methods.clearErrors('password');
        }

        setLoading(true);

        const usernameOrEmailValid = await methods.trigger('usernameOrEmail');

        if (!usernameOrEmailValid) {
            setLoading(false);
            return;
        }

        const isEmail = isPotentialEmail(value);
        if (isEmail) {
            globalBigQueryReporter.enrichContext({ emailDomain: extractEmailDomain(value) });
            await verifyEmailDebounced({
                value,
                setError: methods.setError,
                clearErrors: methods.clearErrors,
            });
        } else {
            setIsSignUp(false);
            await verifyUsernameDebounced({
                value,
                setError: methods.setError,
                clearErrors: methods.clearErrors,
            });
        }
    };

    useEffect(() => {
        const cleanupOnUnmount = () => cleanupDebouncedMethods();
        return cleanupOnUnmount;
    }, [cleanupDebouncedMethods]);

    return {
        loading,
        setLoading,
        isSignUp,
        methods,
        password,
        usernameOrEmail,
        passwordError: errors?.password as UseFormStateReturn['errors']['password'],
        onUsernameOrEmailChange,
    };
};
